import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Pwa } from '@freelancer/pwa';
// This is to prevent circular dependency issues, as both this service and
// camera-input.component.ts require modals.
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { ModalService } from '../modal/modal.service';
import {
  permissionCheck,
  PermissionDenyReason,
  PermissionStatus,
} from './permissions.model';
import type { PermissionType } from './permissions.model';

@Injectable({ providedIn: 'root' })
export class Permissions {
  constructor(
    private modalService: ModalService,
    private pwa: Pwa,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}
  /**
   * For each request function, the first section checks the permission status
   * for native.
   *
   * In the case that it is granted, it will set res = true, and follow
   * the default behaviour of returning res.
   *
   * In the case it has not been granted/denied, it will show the modal,
   * and once the modal has been closed, it will either:
   *
   * a) request permissions using the native library if the
   *    user allowed it on the app modal, or
   * b) set res = false.
   *
   * Otherwise, if it has already been previously denied, it will set res
   * = false.
   *
   * Using res, it will determine whether to show the recovery modal.
   *
   * The second section checks the permission status for browser.
   *
   * This will use the Navigator Permissions API to check for
   * permissions.
   *
   * The reason this is wrapped in a try/catch is because some browsers
   * namely some versions of Safari, might not have access to this API.
   *
   * In the case that a browser does not have access to this API, we
   * will set res = true, which would then not bother requesting
   * using the modal. This should mean that this allows the browser to
   * handle things the existing way - which is by calling the API for a
   * result, which triggers requesting permissions, etc.
   *
   * In the case that it is granted, it will set res = true, and follow
   * the default behaviour of returning res.
   *
   * In the case it has not been granted/denied, it will show the modal,
   * and once the modal has been closed, it will either:
   *
   * a) set res = false, or
   * b) set res = true, and allow the `onchange` function to display
   *    the recovery modal should it be required.
   *
   * The reason that res = true is to bypass showing the recovery
   * modal. This will allow the browser to handle things the existing
   * way - which is by calling the API for a result, which triggers
   * permission request, etc.
   *
   * Otherwise, if it has already been previously denied, it will set res
   * = false.
   *
   * Using res, it will determine whether to show the recovery modal.
   */
  async requestPermissions(permission: PermissionType, reason: string) {
    if (isPlatformBrowser(this.platformId)) {
      let res: boolean | undefined;
      let permissionDenyReason: PermissionDenyReason | undefined;

      if (this.pwa.isNative()) {
        const capacitorPermissionStatus = await permissionCheck[
          permission
        ].nativeCheck();

        if (capacitorPermissionStatus === PermissionStatus.GRANTED) {
          res = true;
        } else if (
          capacitorPermissionStatus === PermissionStatus.PROMPT ||
          capacitorPermissionStatus === PermissionStatus.PROMPT_WITH_RATIONALE
        ) {
          const modalRef = this.modalService.open('PermissionsRequestModal', {
            inputs: {
              permission,
              reason,
            },
            closeable: false,
            showBackdrop: true,
          });

          res = await modalRef.afterClosed().then(modalRes => {
            if (modalRes) {
              return permissionCheck[permission]
                .nativeRequest()
                .then(nativePermission => {
                  if (nativePermission === PermissionStatus.GRANTED) {
                    return true;
                  }
                  permissionDenyReason = PermissionDenyReason.APP;
                  return false;
                });
            }

            permissionDenyReason = PermissionDenyReason.USER_DENIED_FROM_APP;
            return false;
          });
        } else if (capacitorPermissionStatus === PermissionStatus.DENIED) {
          permissionDenyReason = PermissionDenyReason.APP;
          res = false;
        }
      } else {
        // `as any` is required to allow camera permissions to be requested from
        // the very same API.
        try {
          const windowRes = await permissionCheck[permission].webCheck();

          if (windowRes.state === PermissionStatus.GRANTED) {
            res = true;
          } else if (windowRes.state === PermissionStatus.PROMPT) {
            const modalRef = this.modalService.open('PermissionsRequestModal', {
              inputs: {
                permission,
                reason,
              },
              closeable: false,
              showBackdrop: true,
            });

            res = await modalRef.afterClosed().then(modalRes => {
              if (!modalRes) {
                permissionDenyReason =
                  PermissionDenyReason.USER_DENIED_FROM_APP;
                return false;
              }

              windowRes.onchange = (e: Event) => {
                permissionDenyReason = PermissionDenyReason.APP;
                if ((e.target as any).state !== PermissionStatus.GRANTED) {
                  this.modalService.open('PermissionsRecoveryModal', {
                    inputs: {
                      permission,
                      permissionReason: reason,
                      permissionDenyReason,
                    },
                    showBackdrop: true,
                  });
                }
                windowRes.onChange = undefined;
              };

              return true;
            });
          } else if (windowRes.state === PermissionStatus.DENIED) {
            permissionDenyReason = PermissionDenyReason.APP;
            res = false;
          }
        } catch {
          res = true;
        }
      }

      if (!res) {
        this.modalService.open('PermissionsRecoveryModal', {
          inputs: {
            permission,
            permissionReason: reason,
            permissionDenyReason:
              permissionDenyReason ?? PermissionDenyReason.OTHER,
          },
          showBackdrop: true,
        });
      }

      return res ?? false;
    }
    return false;
  }
}
